import React, { FormEvent, useState } from "react";
import { Button, Form, Modal, Toast, ToastContainer } from "react-bootstrap";
import { Link } from "react-router-dom";
import "./styles/VideoReviewsPage.css";
import { websitePaths } from "../../routes/websitePaths";

type ReviewComment = {
  author: string;
  posted: string;
  rating: number;
  text: string;
};

type VideoReview = {
  id: string;
  project: string;
  client: string;
  location: string;
  videoUrl: string;
  comments: ReviewComment[];
};

const videoReviews: VideoReview[] = [
  {
    id: "vr-101",
    project: "Home Renovation",
    client: "Col. Sohan Singh",
    location: "Noida",
    videoUrl: "https://www.youtube.com/embed/wcijoAd-yLA",
    comments: [
      {
        author: "Col. Sohan Singh",
        posted: "2 years ago",
        rating: 5,
        text: "Excellent planning and clean execution. Team was always available for updates.",
      },
      {
        author: "Ritika Singh",
        posted: "2 years ago",
        rating: 4,
        text: "The team remained professional on site and completed the work within the committed timeline.",
      },
    ],
  },
  {
    id: "vr-102",
    project: "Office Interior Setup",
    client: "Pradeep Kumar",
    location: "Gurugram",
    videoUrl: "https://www.youtube.com/embed/wcijoAd-yLA",
    comments: [
      {
        author: "Pradeep Kumar",
        posted: "1 year ago",
        rating: 5,
        text: "Great coordination from design to handover. Quality of finish is impressive.",
      },
      {
        author: "Deepa Arora",
        posted: "1 year ago",
        rating: 4,
        text: "The planning and budget discussion were clear and helped us make decisions quickly.",
      },
    ],
  },
  {
    id: "vr-103",
    project: "Maintenance Contract",
    client: "Akhand Pratap Singh",
    location: "Delhi",
    videoUrl: "https://www.youtube.com/embed/wcijoAd-yLA",
    comments: [
      {
        author: "Akhand Pratap Singh",
        posted: "8 months ago",
        rating: 5,
        text: "The response was quick, and the maintenance work was handled without unnecessary delay.",
      },
      {
        author: "Maya Singh",
        posted: "8 months ago",
        rating: 4,
        text: "Helpful team and better communication than previous vendors.",
      },
    ],
  },
];

const renderStars = (rating: number) => {
  const stars = [];
  for (let i = 1; i <= 5; i += 1) {
    if (rating >= i) {
      stars.push(<i key={`full-${i}`} className='bi bi-star-fill'></i>);
    } else if (rating >= i - 0.5) {
      stars.push(<i key={`half-${i}`} className='bi bi-star-half'></i>);
    } else {
      stars.push(<i key={`empty-${i}`} className='bi bi-star'></i>);
    }
  }
  return stars;
};

const VideoReviewsPage: React.FC = () => {
  const [reviewsData, setReviewsData] = useState<VideoReview[]>(videoReviews);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState<string>("");
  const [reviewForm, setReviewForm] = useState({
    author: "",
    rating: 5,
    text: "",
  });

  const openReviewModal = (videoId: string) => {
    setActiveVideoId(videoId);
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setReviewForm({
      author: "",
      rating: 5,
      text: "",
    });
  };

  const handleSubmitReview = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedAuthor = reviewForm.author.trim();
    const trimmedText = reviewForm.text.trim();
    if (!trimmedAuthor || !trimmedText || !activeVideoId) {
      return;
    }

    setReviewsData((prev) =>
      prev.map((video) =>
        video.id === activeVideoId
          ? {
              ...video,
              comments: [
                {
                  author: trimmedAuthor,
                  rating: reviewForm.rating,
                  text: trimmedText,
                  posted: "Just now",
                },
                ...video.comments,
              ],
            }
          : video,
      ),
    );

    setShowSuccessToast(true);
    closeReviewModal();
  };

  return (
    <section className='video-reviews-page website-page'>
      <div className='video-reviews-hero website-page-hero'>
        <div className='container'>
          <div className='video-reviews-back mb-3'>
            <Link
              to={`${websitePaths.home}#reviews`}
              className='btn btn-outline-light btn-sm'
            >
              <i className='bi bi-arrow-left me-2'></i>
              Back
            </Link>
          </div>
          <div className='video-reviews-head text-center'>
            <p className='website-page-kicker mb-2'>Client Video Library</p>
            <h2 className='website-page-title'>
              Client Reviews And Video Feedback
            </h2>
            <span className='website-page-subtitle'>
              Customer feedback that helps new clients understand our work quality and service experience.
            </span>
          </div>
        </div>
      </div>

      <div className='container website-page-content'>
        <div className='row g-3 mb-4'>
          <div className='col-md-4'>
            <div className='website-metric-card'>
              <h4>Verified Feedback</h4>
              <p>
                Real client experiences across home, commercial, and
                maintenance projects.
              </p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='website-metric-card'>
              <h4>Project Context</h4>
              <p>
                Each review is tied to a project type, client, and location for
                better credibility.
              </p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='website-metric-card'>
              <h4>Open Review Flow</h4>
              <p>
                Visitors can browse comments and share feedback through a clear review form.
              </p>
            </div>
          </div>
        </div>

        <div className='video-review-list'>
          {reviewsData.map((item) => (
            <article className='video-review-card' key={item.id}>
              <div className='video-review-grid'>
                <div className='video-side'>
                  <div className='ratio ratio-16x9 video-frame video-frame--small'>
                    <iframe
                      src={item.videoUrl}
                      title={`${item.project} - ${item.client}`}
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                      referrerPolicy='strict-origin-when-cross-origin'
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                <div className='content-side'>
                  <div className='video-meta mb-3'>
                    <h2>{item.project}</h2>
                    <p className='mb-1'>
                      <strong>Client:</strong> {item.client}
                    </p>
                    <p className='mb-0'>
                      <strong>Location:</strong> {item.location}
                    </p>
                  </div>
                  <div className='review-title-row'>
                    <h3 className='review-title mb-0'>Comments</h3>
                    <button
                      type='button'
                      className='btn btn-sm brand-btn-primary'
                      onClick={() => openReviewModal(item.id)}
                    >
                      Add Review
                    </button>
                  </div>
                  <div className='comment-list'>
                    {item.comments.map((comment, idx) => (
                      <div className='comment-item' key={`${item.id}-${idx}`}>
                        <div className='comment-head'>
                          <strong>{comment.author}</strong>
                          <span>{comment.posted}</span>
                        </div>
                        <div className='comment-rating'>
                          <span>{renderStars(comment.rating)}</span>
                          <b>{comment.rating.toFixed(1)}/5</b>
                        </div>
                        <p>{comment.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <Modal
        show={showReviewModal}
        onHide={closeReviewModal}
        centered
        backdrop={false}
        className='review-modal'
      >
        <Modal.Header closeButton>
          <Modal.Title>Share Your Review</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitReview}>
          <Modal.Body>
            <p className='modal-helper-text'>
              Your feedback helps other customers make better decisions.
            </p>
            <Form.Group className='mb-3' controlId='reviewAuthor'>
              <Form.Label>Your Name</Form.Label>
              <Form.Control
                type='text'
                placeholder='Enter your name'
                value={reviewForm.author}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, author: e.target.value }))
                }
                required
              />
            </Form.Group>

            <Form.Group className='mb-3' controlId='reviewRating'>
              <Form.Label>Rating</Form.Label>
              <Form.Select
                value={reviewForm.rating}
                onChange={(e) =>
                  setReviewForm((prev) => ({
                    ...prev,
                    rating: Number(e.target.value),
                  }))
                }
              >
                <option value={5}>5 - Excellent</option>
                <option value={4}>4 - Very Good</option>
                <option value={3}>3 - Good</option>
                <option value={2}>2 - Fair</option>
                <option value={1}>1 - Poor</option>
              </Form.Select>
            </Form.Group>

            <Form.Group controlId='reviewComment'>
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as='textarea'
                rows={4}
                placeholder='Write your review'
                value={reviewForm.text}
                onChange={(e) =>
                  setReviewForm((prev) => ({ ...prev, text: e.target.value }))
                }
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='outline-secondary' onClick={closeReviewModal}>
              Cancel
            </Button>
            <Button variant='primary' type='submit'>
              Submit Review
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ToastContainer position='bottom-end' className='p-3'>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={3000}
          autohide
          bg='success'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Review Submitted</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            Thank you. Your review has been added successfully.
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </section>
  );
};

export default VideoReviewsPage;
